import { getBEFinSession, loginToBEFin } from 'api/v2/users/beFinances';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCacheKeys } from 'types/cacheKeys.types';
import { logAmplitude } from 'utils/amplitude';
import { WEBAPP_DOCUMENTSREPOSITORY_CLICKEDCONNECMINFIN } from 'utils/amplitude/events/repository.amplitude';
import { useIsBEFinFeatureEnabled } from 'utils/docsRepo/useIsBEFinFeatureEnabled/useIsBEFinFeatureEnabled';
import { ONE_MINUTE } from 'utils/time';
import showToast from 'utils/toast';

const useBEFinSession = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const isFeatureEnabled = useIsBEFinFeatureEnabled();

  const { data, isLoading, refetch } = useQuery(
    [UserCacheKeys.BEFinSession],
    getBEFinSession,
    {
      staleTime: ONE_MINUTE * 5,
      cacheTime: ONE_MINUTE * 30,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: isFeatureEnabled,
    },
  );

  // Refetch session after redirect from MyMinFin and clean URL
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const source = searchParams.get('source');
  const from = searchParams.get('from');

  useEffect(() => {
    if (!isFeatureEnabled) return;
    if (source !== 'myminfin_connect') return;

    logAmplitude(WEBAPP_DOCUMENTSREPOSITORY_CLICKEDCONNECMINFIN, {
      source: from || 'other',
    });

    refetch()
      .then((result) => {
        const newData = result.data;
        const hasIntervat = newData?.consents?.intervatApi || false;
        const hasMmf = newData?.consents?.mmfApi || false;

        // User has MyMinfin but NOT Intervat - show toast suggesting they enable both
        if (hasMmf && !hasIntervat) {
          showToast(
            {
              variant: 'warning',
              title: t('integration.toast.intervat_withdrawn.title'),
              linkButtons: [
                {
                  title: t('integration.toast.intervat_withdrawn.cta'),
                  variant: 'primary',
                  onClick: () => {
                    // Redirect to BEFin login with current page as return URL
                    const redirectUrl = `${window.location.origin}${window.location.pathname}?source=myminfin_connect`;
                    loginToBEFin(redirectUrl).then((res) => {
                      if (res.authUri) {
                        window.location.href = res.authUri;
                      }
                    });
                  },
                },
              ],
            },
            {
              toastId: 'befin-intervat-withdrawn',
              autoClose: 30000, //30secs
              style: {
                width: '35vw',
                maxWidth: '600px',
              },
            },
          );
        }
      })
      .finally(() => {
        // Clean URL params regardless of success or failure
        searchParams.delete('source');
        searchParams.delete('from');
        const newSearch = searchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, {
          replace: true,
        });
      });
  }, [
    isFeatureEnabled,
    source,
    from,
    refetch,
    searchParams,
    location.pathname,
    navigate,
    t,
  ]);

  return {
    sessionData: data,
    hasAnyConsent:
      data?.consents?.mmfApi || data?.consents?.intervatApi || false,
    mmfConsent: data?.consents?.mmfApi || false,
    intervatConsent: data?.consents?.intervatApi || false,
    isManualSyncAvailable: data?.isManualSyncAvailable || false,
    isLoading,
    refetch,
  };
};

export default useBEFinSession;
